import React, { useState, useRef, useEffect } from 'react';
import { useTheme } from '../../contexts/ThemeContext';
import { useSkills } from '../../hooks/useSkills';
import { SkillSearchBar } from './SkillSearchBar';
import { SkillSearchParams, SkillMeta } from '../../types/skill';

interface SkillPickerProps {
  isOpen: boolean;
  onClose: () => void;
  onAttach: (skillNames: string[]) => void;
  currentSkills?: string[];
  tokenBudget?: number;
}

export const SkillPicker: React.FC<SkillPickerProps> = ({
  isOpen,
  onClose,
  onAttach,
  currentSkills = [],
  tokenBudget,
}) => {
  const { darkMode } = useTheme();
  const containerRef = useRef<HTMLDivElement>(null);
  const [searchParams, setSearchParams] = useState<Partial<SkillSearchParams>>({});
  // Map: skill name → estimated tokens (persists across filter page changes)
  const [selected, setSelected] = useState<Map<string, number>>(new Map(
    currentSkills.map(name => [name, 0] as [string, number])
  ));

  const { data, isLoading } = useSkills(searchParams);
  const skills = data?.skills ?? [];

  // Click outside to close
  useEffect(() => {
    if (!isOpen) return;
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        onClose();
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const toggleSkill = (skill: SkillMeta) => {
    setSelected((prev) => {
      const next = new Map(prev);
      if (next.has(skill.name)) next.delete(skill.name);
      else next.set(skill.name, skill.estimated_tokens);
      return next;
    });
  };

  const totalTokens = Array.from(selected.values()).reduce((a, b) => a + b, 0);

  const budgetPct = tokenBudget ? Math.round((totalTokens / tokenBudget) * 100) : null;
  const budgetColor =
    budgetPct === null ? '' : budgetPct > 90 ? 'text-red-500' : budgetPct > 70 ? 'text-amber-500' : 'text-green-500';

  const handleAttach = () => {
    onAttach(Array.from(selected.keys()));
    setSelected(new Map());
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div
        ref={containerRef}
        className={`w-full max-w-2xl mx-4 rounded-lg shadow-2xl border flex flex-col ${
          darkMode ? 'bg-gray-800 border-gray-700 text-white' : 'bg-white border-gray-200 text-gray-900'
        }`}
        style={{ maxHeight: '80vh' }}
      >
        {/* Header */}
        <div className={`p-4 border-b flex items-center justify-between ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
          <h3 className="text-lg font-semibold">Attach Skills</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-200 transition-colors">✕</button>
        </div>

        {/* Search */}
        <div className={`p-4 border-b ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
          <SkillSearchBar onSearch={setSearchParams} />
        </div>

        {/* List */}
        <div className="flex-1 overflow-y-auto p-2">
          {isLoading ? (
            <div className="flex justify-center p-8">
              <div className="animate-spin w-6 h-6 border-2 border-blue-500 border-t-transparent rounded-full" />
            </div>
          ) : skills.length === 0 ? (
            <p className={`text-center py-8 text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
              No skills found
            </p>
          ) : (
            skills.map((skill) => {
              const isAttached = currentSkills.includes(skill.name);
              const isChecked = selected.has(skill.name);
              return (
                <label
                  key={skill.name}
                  className={`flex items-start gap-3 p-3 rounded-md cursor-pointer transition-colors ${
                    isChecked
                      ? darkMode ? 'bg-blue-900/30' : 'bg-blue-50'
                      : darkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-50'
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={isChecked}
                    disabled={isAttached}
                    onChange={() => toggleSkill(skill)}
                    className="mt-0.5 flex-shrink-0"
                  />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium">{skill.display_name}</span>
                      {isAttached && (
                        <span className={`text-xs px-1.5 py-0.5 rounded-full ${darkMode ? 'bg-green-900/40 text-green-300' : 'bg-green-50 text-green-700'}`}>
                          attached
                        </span>
                      )}
                      {skill.estimated_tokens > 0 && (
                        <span className={`text-xs ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                          ~{skill.estimated_tokens.toLocaleString()} tokens
                        </span>
                      )}
                    </div>
                    <p className={`text-xs mt-0.5 line-clamp-1 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                      {skill.description}
                    </p>
                  </div>
                </label>
              );
            })
          )}
        </div>

        {/* Footer */}
        <div className={`p-4 border-t flex items-center justify-between ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
          <div className="text-sm">
            <span className={darkMode ? 'text-gray-300' : 'text-gray-700'}>
              Selected: {selected.size} skill{selected.size !== 1 ? 's' : ''}
            </span>
            {totalTokens > 0 && (
              <span className={`ml-2 ${budgetColor || (darkMode ? 'text-gray-400' : 'text-gray-500')}`}>
                (~{totalTokens.toLocaleString()} tokens{budgetPct !== null ? `, ${budgetPct}% of budget` : ''})
              </span>
            )}
          </div>
          <div className="flex gap-2">
            <button
              onClick={onClose}
              className={`px-3 py-1.5 rounded-md text-sm ${darkMode ? 'bg-gray-700 hover:bg-gray-600' : 'bg-gray-200 hover:bg-gray-300'}`}
            >
              Cancel
            </button>
            <button
              onClick={handleAttach}
              disabled={selected.size === 0}
              className={`px-3 py-1.5 rounded-md text-sm text-white transition-colors ${
                selected.size === 0
                  ? 'bg-blue-400 cursor-not-allowed'
                  : 'bg-blue-500 hover:bg-blue-600'
              }`}
            >
              Attach Selected
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
